<script lang="ts">
	import { Briefcase, MapPin, Contact } from 'lucide-svelte';
	import * as Tooltip from '@/components/ui/tooltip';
	import * as Popover from '@/components/ui/popover';
	import type { CalendarEvent, EventColor } from '@/types/calendar';
	import { isJobEvent, isSiteVisitEvent, isLeadEvent } from '@/types/calendar';
	import { getEventColorClasses, formatDate } from './calendar.utils';
	import { cn } from '@/lib/utils';
	import CalendarEventPopover from './CalendarEventPopover.svelte';

	interface Props {
		event: CalendarEvent;
		color?: EventColor;
		variant?: 'solid' | 'dashed' | 'strikethrough';
		compact?: boolean;
		showPopover?: boolean;
		onclick?: () => void;
		class?: string;
	}

	let {
		event,
		color,
		variant = 'solid',
		compact = false,
		showPopover = true,
		onclick,
		class: className,
	}: Props = $props();

	// Determine event color based on event type and status
	const eventColor = $derived.by((): EventColor => {
		// If explicit color provided, use it
		if (color) return color;

		// For jobs, use the status color from meta
		if (isJobEvent(event) && event.meta?.statusColor) {
			return event.meta.statusColor as EventColor;
		}

		// For leads, use the status color from meta
		if (isLeadEvent(event) && event.meta?.statusColor) {
			return event.meta.statusColor as EventColor;
		}

		// For site visits, use violet
		if (isSiteVisitEvent(event)) {
			return 'violet';
		}

		// Default fallback
		return 'blue';
	});

	// Get color classes based on event color and variant
	const colorClasses = $derived(getEventColorClasses(eventColor, variant));

	// Format date range for tooltip
	const dateRange = $derived.by(() => {
		if (!event.start || !event.end) return '';

		// Extract date portion from ISO string (YYYY-MM-DD)
		const startDate = event.start.split('T')[0];
		const endDate = event.end.split('T')[0];

		if (startDate === endDate) {
			return formatDate(startDate);
		}

		return `${formatDate(startDate)} - ${formatDate(endDate)}`;
	});

	// Base classes for the event container
	const baseClasses = $derived(
		cn(
			'flex items-center gap-2 rounded-md transition-colors',
			compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm',
			colorClasses,
			className,
		),
	);

	// Event icon based on type
	const EventIcon = $derived(
		event.type === 'lead' ? Contact : event.type === 'job' ? Briefcase : MapPin,
	);

	// Display title — for leads/jobs the backend provides event_label (ref - postcode - customer)
	const displayTitle = $derived.by(() => {
		if (isSiteVisitEvent(event) && event.meta?.postcode) {
			return `${event.title} - ${event.meta.postcode}`;
		}
		return event.title;
	});
</script>

{#if showPopover}
	<Popover.Root>
		<Popover.Trigger
			class={cn(baseClasses, 'w-full text-left hover:opacity-80 cursor-pointer')}
			onclick={onclick}
		>
			<EventIcon class="size-4 shrink-0" />
			<span class="truncate font-medium">{displayTitle}</span>
		</Popover.Trigger>
		<Popover.Content class="w-80" side="bottom" align="start" collisionPadding={16}>
			<CalendarEventPopover {event} />
		</Popover.Content>
	</Popover.Root>
{:else if event.meta && 'customerId' in event.meta && event.meta.customerId && !onclick}
	<!-- Render as link for job events with customer ID -->
	<a
		href="/jobs/{event.id}"
		class={cn(baseClasses, 'hover:opacity-80')}
		onclick={(e) => {
			if (onclick) {
				e.preventDefault();
				onclick();
			}
		}}
	>
		<EventIcon class="size-4 shrink-0" />
		<span class="truncate font-medium">{displayTitle}</span>
	</a>
{:else}
	<!-- Render as button with tooltip -->
	<Tooltip.Provider delayDuration={0}>
		<Tooltip.Root>
			<Tooltip.Trigger
				class={cn(baseClasses, 'w-full text-left hover:opacity-80')}
				onclick={onclick}
			>
				<EventIcon class="size-4 shrink-0" />
				<span class="truncate font-medium">{displayTitle}</span>
			</Tooltip.Trigger>
			<Tooltip.Content>
				<div class="space-y-1">
					<p class="font-semibold">{event.title}</p>
					{#if dateRange}
						<p class="text-xs text-muted-foreground">{dateRange}</p>
					{/if}
				</div>
			</Tooltip.Content>
		</Tooltip.Root>
	</Tooltip.Provider>
{/if}